import React, { useCallback, useEffect } from 'react';

import { THEMES } from 'constants/theme.constants';
import { VariantType } from 'theme/variants';
import { storageUtils } from 'utils';

import { DEFAULT_LIGHT_THEME } from '../config/themes';

const THEME_KEY = 'spay-theme';

export type ThemeName = (typeof THEMES)[keyof typeof THEMES];
export type ThemeConfig = Record<ThemeName, VariantType>;

const initialState = {
  themeName: DEFAULT_LIGHT_THEME,
  customThemeConfig: {},
  setThemeName: (themeName: ThemeName) => {},
  initThemes: (data: ThemeConfig) => {},
  toggleAlternativeTheme: () => {},
};

const ThemeContext = React.createContext(initialState);

type ThemeProviderProps = {
  children: React.ReactNode;
};

function ThemeProvider({ children }: ThemeProviderProps) {
  const [themeName, _setThemeName] = React.useState<ThemeName>(
    initialState.themeName,
  );
  const [customThemeConfig, setCustomThemeConfig] = React.useState({});

  const setThemeName = useCallback((themeName: ThemeName) => {
    storageUtils.setItem(THEME_KEY, JSON.stringify(themeName));
    _setThemeName(themeName);
  }, []);

  const initThemes = useCallback((themeConfig: ThemeConfig) => {
    setCustomThemeConfig(themeConfig);
  }, []);

  const toggleAlternativeTheme = useCallback(() => {
    setThemeName(
      themeName === THEMES.DEFAULT ? THEMES.ALTERNATIVE : THEMES.DEFAULT,
    );
  }, [themeName, setThemeName]);

  useEffect(() => {
    const storedTheme = storageUtils.getItem(THEME_KEY);

    if (storedTheme) {
      _setThemeName(JSON.parse(storedTheme));
    } else {
      _setThemeName(DEFAULT_LIGHT_THEME);
    }
  }, []);

  return (
    <ThemeContext.Provider
      value={{
        themeName,
        customThemeConfig,
        setThemeName,
        initThemes,
        toggleAlternativeTheme,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

export { ThemeContext, ThemeProvider };
